import { type UnknownAction } from "@reduxjs/toolkit";
import {
  AUTHENTICATE_USER,
  AUTHENTICATING_USER_FAILED,
  AUTHENTICATING_USER_SUCCEEDED,
  CHANGING_PASSWORD,
  CHANGING_PASSWORD_FAILED,
  CHANGING_PASSWORD_SUCCEEDED,
  FETCHING_ACTIVE_USERS_COUNT,
  FETCHING_ACTIVE_USERS_COUNT_FAILED,
  FETCHING_ACTIVE_USERS_COUNT_SUCCEEDED,
  FETCHING_USER_PROFILE_SUCCEDDED,
  FETCHING_USERS,
  FETCHING_USERS_FAILED,
  FETCHING_USERS_SUCCEEDED,
  UPDATING_EMAIL_SIGNATURE,
  UPDATING_EMAIL_SIGNATURE_FAILED,
  UPDATING_EMAIL_SIGNATURE_SUCCEEDED,
  UPDATING_USER_PROFILE,
  UPDATING_USER_PROFILE_FAILED,
  UPDATING_USER_PROFILE_SUCCEDDED,
  FETCH_SSO_URL_REQUEST,
  FETCHING_SSO_URL_SUCCEEDED,
  FETCHING_SSO_URL_FAILED,
  SSO_AUTHENTICATION_FAILED,
  FORGOT_PASSWORD_REQUEST,
  FORGOT_PASSWORD_SUCCEEDED,
  FORGOT_PASSWORD_FAILED,
  SIGNUP_USER_REQUEST,
  SIGNUP_USER_SUCCEEDED,
  SIGNUP_USER_FAILED,
  USER_ACTIVATION_REQUEST,
  USER_ACTIVATION_SUCCEEDED,
  USER_ACTIVATION_FAILED,
  VALIDATE_REGISTRATION,
  VALIDATING_REGISTRATION_SUCCEEDED,
  VALIDATING_REGISTRATION_FAILED,
  WS_SAMPLE_DATA_STREAM_COMPLETION,
  VALIDATING_PASSWORD_RESET_LINK,
  VALIDATING_PASSWORD_RESET_LINK_SUCCEEDED,
  VALIDATING_PASSWORD_RESET_LINK_FAILED,
  FETCH_USER_INVITATION,
  FETCHING_USER_INVITATION_SUCCEEDED,
  FETCHING_USER_INVITATION_FAILED,
  REGISTER_INVITED_USER,
  REGISTERING_INVITED_USER_SUCCEEDED,
  REGISTERING_INVITED_USER_FAILED,
  INVALIDATE_FORGOT_PASSWORD,
  FETCHING_USER_NOTIFICATION_PREFERENCES,
  FETCHING_USER_NOTIFICATION_PREFERENCES_SUCCEEDED,
  FETCHING_USER_NOTIFICATION_PREFERENCES_FAILED,
  UPDATING_USER_NOTIFICATION_PREFERENCES,
  UPDATING_USER_NOTIFICATION_PREFERENCES_SUCCEEDED,
  UPDATING_USER_NOTIFICATION_PREFERENCES_FAILED,
  RESET_PASSWORD,
  RESETTING_PASSWORD_SUCCEEDED,
  RESETTING_PASSWORD_FAILED,
  INVALIDATE_USER_NOTIFICATION_PREFERENCES_UPDATE,
} from "../actions/actionTypes";
import { UserInvitation, UserProfile, UserSSO } from "../../types/users";
import AuthService from "src/utils/auth.service";
import _ from "lodash";

interface UserState {
  authentication: {
    authenticating: boolean;
    authenticated: boolean;
    error: any;
  };
  notificationPreferences: {
    item: any;
    loading: boolean;
    error: any;
    updated: boolean;
    updating: boolean;
  };
  updation: {
    updating: boolean;
    updated: boolean;
    error: any;
  };
  changePassword: {
    changing: boolean;
    changed: boolean;
    error: any;
  };
  resetPassword: {
    changing: boolean;
    changed: boolean;
    error: any;
  };
  emailSignature: {
    updating: boolean;
    updated: boolean;
    error: any;
  };
  me: UserProfile | null;
  users: {
    loading: boolean;
    items: any;
    activeUsers: number;
    error: any;
    pagination: {
      currentPage: number;
      totalPages: number;
    };
  };
  sso: {
    loading: boolean;
    url: string;
    error: boolean;
    ssoAuthFailed: {
      reason: any;
    };
  };
  forgotPassword: {
    loading: boolean;
    success: boolean;
  };
  registration: {
    loading: boolean;
    success: boolean;
    item: any;
    error: {
      code: number | null;
      message: string;
      field: string;
    };
  };
  validate: {
    loading: boolean;
    success: boolean;
    error?: {
      code: string;
      message: string;
    };
  };
  activation: {
    loading: boolean;
    success: boolean;
    error?: {
      code: string;
      message: string;
    };
    sampleDataGenerated?: boolean;
  };
  invitation: UserInvitation;
}

const initialState: UserState = {
  authentication: {
    authenticating: false,
    authenticated: false,
    error: null,
  },
  notificationPreferences: {
    item: {},
    loading: false,
    error: null,
    updated: false,
    updating: false,
  },
  updation: {
    updating: false,
    updated: false,
    error: null,
  },
  changePassword: {
    changing: false,
    changed: false,
    error: null,
  },
  resetPassword: {
    changing: false,
    changed: false,
    error: null,
  },
  emailSignature: {
    updating: false,
    updated: false,
    error: null,
  },
  me: null,
  users: {
    loading: false,
    items: [],
    error: null,
    activeUsers: 0,
    pagination: {
      currentPage: -1,
      totalPages: 1,
    },
  },
  sso: {
    loading: false,
    url: "",
    error: false,
    ssoAuthFailed: {
      reason: null,
    }
  },
  forgotPassword: {
    loading: false,
    success: false,
  },
  registration: {
    loading: false,
    item: null,
    success: false,
    error: {
      code: null,
      message: "",
      field: "",
    },
  },
  validate: {
    loading: false,
    success: false,
    error: {
      code: "",
      message: "",
    },
  },
  activation: {
    loading: false,
    success: false,
    error: {
      code: "",
      message: "",
    },
    sampleDataGenerated: false,
  },
  invitation: {
    loading: false,
    item: {
      id: "",
      email: "",
      admin: false,
      canCreateStream: false,
      canViewReports: false,
      cleverstory: false,
      invitedDate: "",
      ssoLoginMandatory: false,
      userRoles: [],
      userGroups: [],
      entityType: "",
    },
    error: {
      code: null,
      message: "",
    },
  },
};

export function usersReducer(
  state: UserState = initialState,
  action: UnknownAction,
): UserState {
  switch (action.type) {
    case AUTHENTICATE_USER: {
      return {
        ...state,
        authentication: {
          authenticating: true,
          authenticated: false,
          error: null,
        },
      };
    }
    case AUTHENTICATING_USER_SUCCEEDED: {
      const response = action.payload as any;

      AuthService.saveToken(response.access_token, response.refresh_token);

      return {
        ...state,
        authentication: {
          authenticating: false,
          authenticated: true,
          error: null,
        },
      };
    }
    case AUTHENTICATING_USER_FAILED: {
      const { errors } = action.payload as any;
      return {
        ...state,
        authentication: {
          authenticating: false,
          authenticated: false,
          error: {
            code: errors.errors[0].code,
            message: errors.errors[0].message,
          },
        },
      };
    }

    case FETCHING_USER_PROFILE_SUCCEDDED: {
      const response = action.payload as UserProfile;
      return {
        ...state,
        me: response,
      };
    }

    case FETCHING_USER_NOTIFICATION_PREFERENCES: {
      return {
        ...state,
        notificationPreferences: {
          ...state.notificationPreferences,
          loading: true,
        },
      };
    }

    case FETCHING_USER_NOTIFICATION_PREFERENCES_SUCCEEDED: {
      const response = action.payload;

      return {
        ...state,
        notificationPreferences: {
          ...state.notificationPreferences,
          loading: false,
          item: response,
        },
      };
    }

    case FETCHING_USER_NOTIFICATION_PREFERENCES_FAILED: {
      return {
        ...state,
        notificationPreferences: {
          ...state.notificationPreferences,
          loading: false,
          error: action.payload,
        },
      };
    }

    case UPDATING_USER_NOTIFICATION_PREFERENCES: {
      return {
        ...state,
        notificationPreferences: {
          ...state.notificationPreferences,
          loading: true,
          updated: false,
          updating: true,
        },
      };
    }

    case UPDATING_USER_NOTIFICATION_PREFERENCES_SUCCEEDED: {
      return {
        ...state,
        notificationPreferences: {
          ...state.notificationPreferences,
          item: action.payload,
          loading: false,
          updated: true,
          updating: false,
        },
      };
    }

    case UPDATING_USER_NOTIFICATION_PREFERENCES_FAILED: {
      return {
        ...state,
        notificationPreferences: {
          ...state.notificationPreferences,
          loading: false,
          error: action.payload,
          updated: false,
          updating: false,
        },
      };
    }

    case INVALIDATE_USER_NOTIFICATION_PREFERENCES_UPDATE:{
      return {
        ...state,
        notificationPreferences: {
          ...state.notificationPreferences,
          loading: false,
          updated: false,
          updating: false,
        },
      };
    }

    case UPDATING_USER_PROFILE: {
      return {
        ...state,
        updation: {
          updating: true,
          updated: false,
          error: null,
        },
      };
    }

    case UPDATING_USER_PROFILE_SUCCEDDED: {
      return {
        ...state,
        updation: {
          updating: false,
          updated: true,
          error: null,
        },
        me: {
          ..._.get(state, "me", {}),
          ...(action.payload as UserProfile),
        },
      };
    }

    case UPDATING_USER_PROFILE_FAILED: {
      return {
        ...state,
        updation: {
          updating: false,
          updated: false,
          error: action.payload,
        },
      };
    }

    case CHANGING_PASSWORD: {
      return {
        ...state,
        changePassword: {
          changing: true,
          changed: false,
          error: null,
        },
      };
    }

    case CHANGING_PASSWORD_SUCCEEDED: {
      AuthService.clearAccessToken();
      return {
        ...state,
        changePassword: {
          changing: false,
          changed: true,
          error: null,
        },
      };
    }

    case CHANGING_PASSWORD_FAILED: {
      return {
        ...state,
        changePassword: {
          changing: false,
          changed: false,
          error: action.payload,
        },
      };
    }

    case RESET_PASSWORD: {
      return {
        ...state,
        resetPassword: {
          changing: true,
          changed: false,
          error: null,
        },
      };
    }

    case RESETTING_PASSWORD_SUCCEEDED: {
      return {
        ...state,
        resetPassword: {
          changing: false,
          changed: true,
          error: null,
        },
      };
    }

    case RESETTING_PASSWORD_FAILED: {
      return {
        ...state,
        resetPassword: {
          changing: false,
          changed: false,
          error: action.payload,
        },
      };
    }

    case UPDATING_EMAIL_SIGNATURE: {
      return {
        ...state,
        emailSignature: {
          updating: true,
          updated: false,
          error: null,
        },
      };
    }

    case UPDATING_EMAIL_SIGNATURE_SUCCEEDED: {
      return {
        ...state,
        emailSignature: {
          updating: false,
          updated: true,
          error: null,
        },
        me: {
          ..._.get(state, 'me'),
          ...action.payload as UserProfile,
        },
      };
    }

    case UPDATING_EMAIL_SIGNATURE_FAILED: {
      return {
        ...state,
        emailSignature: {
          updating: false,
          updated: false,
          error: action.payload,
        },
      };
    }

    case FETCHING_ACTIVE_USERS_COUNT: {
      return {
        ...state,
      };
    }

    case FETCHING_ACTIVE_USERS_COUNT_SUCCEEDED: {
      return {
        ...state,
        users: {
          ...state.users,
          activeUsers: action.payload as number,
        },
      };
    }

    case FETCHING_ACTIVE_USERS_COUNT_FAILED: {
      return {
        ...state,
        users: {
          ...state.users,
          activeUsers: 0,
          error: action.payload,
        },
      };
    }

    case FETCHING_USERS: {
      return {
        ...state,
        users: {
          ...state.users,
          loading: true,
        },
      };
    }

    case FETCHING_USERS_SUCCEEDED: {
      const response = action.payload as any;
      let items: any[] = [];

      if (response.currentPage != 0) {
        items = [...state.users.items, ...response.records];
      } else {
        items = response.records;
      }

      items = items.reduce((x, y) => {
        const index = x.findIndex((e) => {
          return e.id == y.id;
        });

        if (index < 0) {
          return [...x, y];
        } else {
          x[index] = y;
          return x;
        }
      }, []);

      return {
        ...state,
        users: {
          ...state.users,
          loading: false,
          items: items,
          pagination: {
            ...state.users.pagination,
            currentPage: response.currentPage,
            totalPages: response.totalPages,
          },
        },
      };
    }

    case FETCHING_USERS_FAILED: {
      return {
        ...state,
        users: {
          ...state.users,
          loading: false,
          error: action.payload,
        },
      };
    }

    case FETCH_SSO_URL_REQUEST:
      return {
        ...state,
        sso: {
          ...state.sso,
          loading: true,
        },
      };

    case FETCHING_SSO_URL_SUCCEEDED: {
      const { url } = action.payload as UserSSO;

      return {
        ...state,
        sso: {
          ...state.sso,
          loading: false,
          url: url,
        },
      };
    }

    case FETCHING_SSO_URL_FAILED: {
      return {
        ...state,
        sso: {
          ...state.sso,
          loading: false,
          url: "",
          error: true,
        },
      };
    }

    case SSO_AUTHENTICATION_FAILED: {
      const { reason } = action.payload as any;
      const payload = _.get(reason, "payload");

      return {
        ...state,
        sso: {
          ...state.sso,
          loading: false,
          url: "",
          ssoAuthFailed: {
            reason: payload,
          },
        },
      };
    }

    case FORGOT_PASSWORD_REQUEST: {
      return {
        ...state,
        forgotPassword: {
          loading: true,
          success: false,
        },
      };
    }

    case FORGOT_PASSWORD_SUCCEEDED: {
      return {
        ...state,
        forgotPassword: {
          loading: false,
          success: true,
        },
      };
    }

    case FORGOT_PASSWORD_FAILED: {
      return {
        ...state,
        forgotPassword: {
          loading: false,
          success: false,
        },
      };
    }

    case INVALIDATE_FORGOT_PASSWORD: {
      return {
        ...state,
        forgotPassword: {
          loading: false,
          success: false,
        },
      };
    }

    case SIGNUP_USER_REQUEST: {
      return {
        ...state,
        registration: {
          loading: true,
          success: false,
          item: null,
          error: {
            code: null,
            message: "",
            field: "",
          },
        },
      };
    }

    case SIGNUP_USER_SUCCEEDED: {
      return {
        ...state,
        registration: {
          loading: false,
          success: true,
          item: action.payload,
          error: {
            code: null,
            message: "",
            field: "",
          },
        },
      };
    }

    case SIGNUP_USER_FAILED: {
      const errors = action.payload as any;
      const error = errors.errors[0];
      return {
        ...state,
        registration: {
          loading: false,
          item: null,
          success: false,
          error: {
            code: error.code,
            message: error.message,
            field: error.field,
          },
        },
      };
    }

    case USER_ACTIVATION_REQUEST: {
      return {
        ...state,
        activation: {
          loading: true,
          success: false,
        },
      };
    }

    case USER_ACTIVATION_SUCCEEDED: {
      const { access_token, refresh_token } = action.payload as any;
      AuthService.saveToken(access_token, refresh_token);

      return {
        ...state,
        activation: {
          loading: false,
          success: true,
        },
      };
    }

    case USER_ACTIVATION_FAILED: {
      const errors = action.payload as any;

      return {
        ...state,
        activation: {
          loading: false,
          success: false,
          error: {
            code: errors.errors[0].code,
            message: errors.errors[0].message,
          },
        },
      };
    }

    case VALIDATE_REGISTRATION: {
      return {
        ...state,
        validate: {
          loading: true,
          success: false,
        },
      };
    }

    case VALIDATING_REGISTRATION_SUCCEEDED: {
      return {
        ...state,
        validate: {
          loading: false,
          success: true,
        },
      };
    }

    case VALIDATING_REGISTRATION_FAILED: {
      const errors = action.payload as any;

      return {
        ...state,
        validate: {
          loading: false,
          success: false,
          error: {
            code: errors.errors[0].code,
            message: errors.errors[0].message,
          },
        },
      };
    }

    case WS_SAMPLE_DATA_STREAM_COMPLETION:
      return {
        ...state,
        activation: {
          ...state.activation,
          sampleDataGenerated: true,
        },
      };

    case VALIDATING_PASSWORD_RESET_LINK:
      return {
        ...state,
        validate: {
          loading: true,
          success: false,
        },
      };

    case VALIDATING_PASSWORD_RESET_LINK_SUCCEEDED:
      return {
        ...state,
        validate: {
          loading: false,
          success: true,
        },
      };

    case VALIDATING_PASSWORD_RESET_LINK_FAILED:
      return {
        ...state,
        validate: {
          loading: false,
          success: false,
          error: action.payload as any,
        },
      };

    case FETCH_USER_INVITATION:
      return {
        ...state,
        invitation: {
          ...state.invitation,
          loading: true,
        },
      };

    case FETCHING_USER_INVITATION_SUCCEEDED:
      return {
        ...state,
        invitation: {
          ...state.invitation,
          loading: false,
          item: action.payload as any,
        },
      };

    case FETCHING_USER_INVITATION_FAILED: {
      const errors = action.payload as any;
      const error = errors.errors[0] as any;

      return {
        ...state,
        invitation: {
          ...state.invitation,
          loading: false,
          error: error,
        },
      };
    }

    case REGISTER_INVITED_USER:
      return {
        ...state,
        registration: {
          loading: true,
          item: null,
          success: false,
          error: {
            code: null,
            message: "",
            field: "",
          },
        },
      };

    case REGISTERING_INVITED_USER_SUCCEEDED:
      return {
        ...state,
        registration: {
          loading: false,
          item: action.payload,
          success: true,
          error: {
            code: null,
            message: "",
            field: "",
          },
        },
      };

    case REGISTERING_INVITED_USER_FAILED: {
      const errors = action.payload as any;
      const error = errors.errors[0] as any;

      return {
        ...state,
        registration: {
          loading: false,
          item: null,
          success: false,
          error: {
            code: error.code,
            message: error.message,
            field: error.field,
          },
        },
      };
    }

    default: {
      return state;
    }
  }
}
